import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db, auth } from './firebase';
import './TShirtRecon.css';

function TShirtRecon({ onClose }) {
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [franchiseeName, setFranchiseeName] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);

      const frDoc = await getDoc(franchiseeRef);
      if (frDoc.exists()) setFranchiseeName(frDoc.data().name || '');

      const ordersSnap = await getDocs(
        query(collection(db, 'tshirtOrders'), where('franchiseeRef', '==', franchiseeRef))
      );
      const ordersList = ordersSnap.docs.map(d => ({
        id: d.id,
        ...d.data(),
        orderDateParsed: d.data().orderDate?.toDate
          ? d.data().orderDate.toDate()
          : new Date(d.data().orderDate || Date.now())
      }));
      ordersList.sort((a, b) => a.orderDateParsed - b.orderDateParsed);
      setOrders(ordersList);

      const stockSnap = await getDocs(
        query(collection(db, 'tshirtStock'), where('franchiseeRef', '==', franchiseeRef))
      );
      setStockItems(stockSnap.docs.map(d => ({ id: d.id, ...d.data() })));

    } catch (err) {
      console.error('Error loading recon data:', err);
      alert('Error loading data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fmt = (n) => `R${(n || 0).toFixed(2)}`;
  const fmtDate = (d) => {
    if (!d) return '—';
    const dt = d instanceof Date ? d : new Date(d);
    return isNaN(dt) ? '—' : dt.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Map stock docs to their source order
  const stockByOrder = {};
  stockItems.forEach(s => {
    if (!stockByOrder[s.sourceOrderId]) stockByOrder[s.sourceOrderId] = [];
    stockByOrder[s.sourceOrderId].push(s);
  });

  const grandTotalPaid  = orders.reduce((s, o) => s + (o.totalCost || 0), 0);
  const grandAllocated  = stockItems.reduce((s, x) => s + (x.allocated || 0), 0);
  const grandStockLeft  = stockItems.reduce((s, x) => s + (x.remaining || 0), 0);
  const grandExtraCost  = orders.reduce((s, o) => s + (o.extraStockCost || 0), 0);

  if (loading) {
    return (
      <div className="recon-page">
        <div className="recon-loading">
          <div className="recon-spinner" />
          <p>Loading reconciliation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="recon-page">

      {/* PAGE HEADER */}
      <div className="recon-page-header">
        <div>
          <h1>👕 T-Shirt Order Reconciliation</h1>
          {franchiseeName && (
            <p>Franchisee: <strong>{franchiseeName}</strong> &nbsp;·&nbsp; Generated: {fmtDate(new Date())}</p>
          )}
        </div>
      </div>

      {/* LEGEND */}
      <div className="recon-legend-bar">
        <span className="badge-new">🆕 New Order</span>
        <span className="legend-desc">shirt ordered &amp; billed at R120</span>
        <span className="legend-sep">|</span>
        <span className="badge-stock">📦 Stock</span>
        <span className="legend-desc">taken from existing stock — <strong>no new charge</strong></span>
      </div>

      {orders.length === 0 && (
        <div className="recon-empty">No orders found for your account.</div>
      )}

      {/* ONE BLOCK PER ORDER */}
      {orders.map((order, idx) => {
        const stockForOrder = stockByOrder[order.id] || [];

        // Rows: students in order
        const newRows = (order.studentTshirts || []).map(st => ({
          studentName: st.studentName,
          schoolName:  st.schoolName || '—',
          size:        st.size,
          source:      'new',
          date:        order.orderDateParsed
        }));

        // Rows: students allocated from THIS order's stock
        const stockRows = stockForOrder.flatMap(stockDoc =>
          (stockDoc.allocations || []).map(alloc => ({
            studentName: alloc.studentName,
            schoolName:  '—',
            size:        stockDoc.size,
            source:      'stock',
            date:        alloc.allocatedDate?.toDate
              ? alloc.allocatedDate.toDate()
              : alloc.allocatedDate ? new Date(alloc.allocatedDate) : null
          }))
        );

        const allRows = [...newRows, ...stockRows];

        const newShirtsCount  = (order.studentTshirtCount || 0) + (order.extraStockCount || 0);
        const stockAllocCount = stockRows.length;
        const stockLeft       = stockForOrder.reduce((s, x) => s + (x.remaining || 0), 0);

        // Extra stock sizes (buffer stock ordered)
        const extraEntries = order.extraStock
          ? Object.entries(order.extraStock).filter(([, q]) => q > 0)
          : [];

        return (
          <div key={order.id} className="order-block">

            {/* Order heading */}
            <div className="order-block-header">
              <span className="order-block-num">Order {idx + 1} — {order.orderNumber || ''}</span>
              <span className="order-block-date">{fmtDate(order.orderDateParsed)}</span>
            </div>

            {/* Student table */}
            {allRows.length > 0 ? (
              <div className="table-wrap">
                <table className="recon-table">
                  <thead>
                    <tr>
                      <th>Student Name</th>
                      <th>School</th>
                      <th>Size Ordered</th>
                      <th>Source</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allRows.map((row, i) => (
                      <tr key={i} className={row.source === 'stock' ? 'stock-row' : ''}>
                        <td><strong>{row.studentName}</strong></td>
                        <td>{row.schoolName}</td>
                        <td className="size-cell">{row.size}</td>
                        <td>
                          {row.source === 'new'
                            ? <span className="badge-new">🆕 New Order</span>
                            : <span className="badge-stock">📦 Stock</span>}
                        </td>
                        <td>{fmtDate(row.date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="recon-empty-inner">No student shirts linked to this order yet.</div>
            )}

            {/* Extra stock chip row (unallocated buffer stock from this order) */}
            {extraEntries.length > 0 && (
              <div className="extra-stock-bar">
                <span className="esb-label">📦 Extra buffer stock in this order:</span>
                {extraEntries.map(([size, qty]) => {
                  const sd = stockForOrder.find(s => s.size === size);
                  const alloc = sd ? (sd.allocated || 0) : 0;
                  const rem   = sd ? (sd.remaining || 0) : qty;
                  return (
                    <span key={size} className="esb-chip">
                      {size}: <strong>{qty}</strong> ordered
                      {alloc > 0 && <span className="chip-alloc"> · {alloc} allocated</span>}
                      {rem > 0
                        ? <span className="chip-rem"> · {rem} in stock</span>
                        : <span className="chip-done"> · fully used</span>}
                    </span>
                  );
                })}
              </div>
            )}

            {/* ORDER SUMMARY */}
            <div className="order-summary-box">
              <div className="osb-row osb-new">
                <span className="osb-label">🆕 New shirts ordered</span>
                <span className="osb-count">{newShirtsCount} shirt{newShirtsCount !== 1 ? 's' : ''}</span>
                <span className="osb-amount">{fmt(order.totalCost)}</span>
              </div>

              {stockAllocCount > 0 && (
                <div className="osb-row osb-stock">
                  <span className="osb-label">📦 Shirts allocated from stock</span>
                  <span className="osb-count">{stockAllocCount} shirt{stockAllocCount !== 1 ? 's' : ''}</span>
                  <span className="osb-amount osb-free">
                    R0.00 <span className="osb-note">already paid for</span>
                  </span>
                </div>
              )}

              {stockLeft > 0 && (
                <div className="osb-row osb-rem">
                  <span className="osb-label">🏪 Stock still available</span>
                  <span className="osb-count">{stockLeft} shirt{stockLeft !== 1 ? 's' : ''}</span>
                  <span className="osb-amount osb-free">
                    R0.00 <span className="osb-note">no charge when allocated</span>
                  </span>
                </div>
              )}

              <div className="osb-total">
                <span>💳 Amount charged for this order</span>
                <span className="osb-total-amt">{fmt(order.totalCost)}</span>
              </div>
            </div>

          </div>
        );
      })}

      {/* GRAND SUMMARY */}
      {orders.length > 0 && (
        <div className="grand-summary">
          <h2>📊 Overall Summary</h2>

          <div className="table-wrap">
            <table className="recon-table grand-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Date</th>
                  <th>New Shirts Billed</th>
                  <th>Stock Allocated (no charge)</th>
                  <th>Amount Paid</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order, idx) => {
                  const sf       = stockByOrder[order.id] || [];
                  const alloc    = sf.reduce((s, x) => s + (x.allocated || 0), 0);
                  const newCount = (order.studentTshirtCount || 0) + (order.extraStockCount || 0);
                  return (
                    <tr key={order.id}>
                      <td><strong>Order {idx + 1}</strong> &nbsp;{order.orderNumber || ''}</td>
                      <td>{fmtDate(order.orderDateParsed)}</td>
                      <td><span className="badge-new">🆕 {newCount} shirt{newCount !== 1 ? 's' : ''}</span></td>
                      <td>
                        {alloc > 0
                          ? <span className="badge-stock">📦 {alloc} shirt{alloc !== 1 ? 's' : ''}</span>
                          : <span className="dim">—</span>}
                      </td>
                      <td className="grand-amt">{fmt(order.totalCost)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="grand-foot">
                  <td colSpan="2"><strong>GRAND TOTAL</strong></td>
                  <td><strong>{orders.reduce((s, o) => s + (o.studentTshirtCount || 0) + (o.extraStockCount || 0), 0)} shirts</strong></td>
                  <td>
                    {grandAllocated > 0
                      ? <><strong>{grandAllocated} shirts</strong> <span className="dim">(no charge)</span></>
                      : '—'}
                  </td>
                  <td className="grand-amt grand-final">{fmt(grandTotalPaid)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Stock in hand callout */}
          {grandStockLeft > 0 && (
            <div className="info-box info-purple">
              📦 You still have <strong>{grandStockLeft} shirt{grandStockLeft !== 1 ? 's' : ''}</strong> in stock,
              already paid for ({fmt(grandStockLeft * 120)}).
              When you allocate these to students, <strong>no new charge will be raised.</strong>
            </div>
          )}

          {/* Clarification bullets */}
          <div className="clarification-box">
            <h3>📌 Key Points</h3>
            <ul>
              <li>
                <span className="badge-new">🆕 New Order</span> &nbsp;
                shirts are billed at R120 each at the time of ordering.
                Student shirts are covered by their R160 admin fee — you collect this from the student.
              </li>
              <li>
                <span className="badge-stock">📦 Stock</span> &nbsp;
                allocations use shirts from extra stock you already purchased.
                <strong> No second invoice is ever raised for these.</strong>
              </li>
              {grandExtraCost > 0 && (
                <li>
                  Your extra buffer stock cost <strong>{fmt(grandExtraCost)}</strong> in total.
                  This is already included in your grand total of <strong>{fmt(grandTotalPaid)}</strong>.
                </li>
              )}
            </ul>
          </div>
        </div>
      )}

    </div>
  );
}

export default TShirtRecon;